// `avatar autopilot run` — tách khỏi commands/autopilot.ts (06k C1). Mặc định
// (không cờ mới) giữ nguyên hành vi cũ từng byte (I1). Thêm:
//   --json            1 dòng máy cuối `{command, result}` (C4); log người ⇒ stderr.
//   --detach          cha sinh con tách session (C2), in biên nhận, KHÔNG giành lock.
//   --detached-child  (ẩn) con của --detach: bỏ SIGHUP (C5), lỗi phân loại ⇒ 70–73 (B11).
//   --run-id <hex>    (ẩn) nonce cha giao cho con; `last-run.json` CAS theo nó (C3).
//   --log-path <p>    (ẩn) log của con — ghi vào last-run, con giành lock xoay log (L-04).
// Cờ ẩn dùng argv, không env `AVATAR_*` (env lọt sang worker qua allowlist).
import { join } from "node:path";
import { type Command, Option } from "commander";
import { parsePositiveTickCount } from "../lib/autopilot/cli-positive-integer-option.js";
import { addStdinValues } from "../lib/autopilot/mission-known-secret-values-registry.js";
import { resolveMission } from "../lib/autopilot/mission-resolver.js";
import { preflightMissionTestEnvForRun } from "../lib/autopilot/mission-secrets-test-env-run-preflight.js";
import {
  type ReleaseSecretsStdin,
  readReleaseSecretsFromStdin,
} from "../lib/autopilot/release-secrets-stdin-reader.js";
import { runAutopilotRunCommand } from "../lib/autopilot/run-command-cli-wiring.js";
import { buildRunJsonResult } from "../lib/autopilot/run-command-json-result.js";
import {
  lastRunOnLockAcquired,
  runJsonFallback,
} from "../lib/autopilot/run-command-last-run-tracking.js";
import { runCommandExitCode } from "../lib/autopilot/run-command-outcome-observability.js";
import {
  type DetachedChildErrorKind,
  machineLockErrorKind,
  runFailureExitCode,
} from "../lib/autopilot/run-detached-child-exit-codes.js";
import {
  detachedReceiptExitCode,
  launchDetachedRun,
} from "../lib/autopilot/run-detached-launcher.js";
import { newRunId, writeLastRunExited } from "../lib/autopilot/run-last-outcome-file.js";
import type { RunMissionResult } from "../lib/autopilot/supervisor-mission-run.js";
import { writeMachineReadableJson } from "../lib/machine-readable-json-output-contract.js";
import { log, setMachineReadableMode } from "../lib/terminal-logger.js";

export interface AutopilotRunCommandDeps {
  resolveWorkspaceRoot: (repoFlag: string | undefined) => Promise<string>;
  machineLockPath: string;
  globalConfigPath: string;
}

interface RunOpts {
  mission?: string;
  repo?: string;
  once?: boolean;
  maxTicks?: number;
  releaseSecretsStdin?: boolean;
  detach?: boolean;
  json?: boolean;
  detachedChild?: boolean;
  runId?: string;
  logPath?: string;
}

const COMMAND = "autopilot run";

/** Lỗi đã phân loại trước dispatch: thông điệp stderr + mã (70–73 chỉ cho con tách rời). */
function failRun(kind: DetachedChildErrorKind, message: string, opts: RunOpts): never {
  process.stderr.write(`✗ ${message}\n`);
  process.exit(runFailureExitCode(kind, opts.detachedChild === true));
}

function forwardArgs(opts: RunOpts, maxTicks: number | undefined): string[] {
  const args: string[] = [];
  if (opts.repo !== undefined) args.push("--repo", opts.repo);
  if (maxTicks !== undefined) args.push("--max-ticks", String(maxTicks));
  if (opts.releaseSecretsStdin === true) args.push("--release-secrets-stdin");
  return args;
}

export function registerAutopilotRunCommand(
  autopilot: Command,
  deps: AutopilotRunCommandDeps,
): void {
  autopilot
    .command("run")
    .description("Chạy supervisor foreground cho một mission (dispatch tới idle)")
    .option("--mission <id>", "Mission ID")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--once", "Chạy đúng 1 tick rồi dừng (tương đương --max-ticks 1)")
    .option("--max-ticks <n>", "Giới hạn số tick control loop (test/E2E)", parsePositiveTickCount)
    .option(
      "--release-secrets-stdin",
      "Đọc secret release (KEY=VALUE) từ stdin pipe, chỉ giữ trong bộ nhớ — cho deploy/verify sau G3",
    )
    .option("--detach", "Chạy nền tách session (sống qua đóng cửa sổ chat), in biên nhận rồi thoát")
    .option("--json", "In đúng 1 dòng JSON kết quả cuối ra stdout (log người ⇒ stderr)")
    .addOption(new Option("--detached-child").hideHelp())
    .addOption(new Option("--run-id <nonce>").hideHelp())
    .addOption(new Option("--log-path <path>").hideHelp())
    .action(async (opts: RunOpts) => {
      if (opts.json === true) setMachineReadableMode(true);
      const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);
      const resolved = await resolveMission({ workspaceRoot, explicitMissionId: opts.mission });
      const maxTicks = opts.once ? 1 : opts.maxTicks;

      // N27: cha --detach rẽ nhánh TRƯỚC preflight + đọc stdin (con tự làm cả hai).
      if (opts.detach === true && opts.detachedChild !== true) {
        const receipt = await launchDetachedRun({
          missionId: resolved.missionId,
          controlRoot: resolved.controlRoot,
          machineLockPath: deps.machineLockPath,
          forwardArgs: forwardArgs(opts, maxTicks),
          releaseSecretsStdin: opts.releaseSecretsStdin === true,
        });
        if (opts.json === true) writeMachineReadableJson({ command: COMMAND, detached: receipt });
        else
          log.info(
            `Mission ${resolved.missionId} chạy nền: started=${receipt.started} pid=${receipt.pid} log=${receipt.log_path}`,
          );
        process.exit(detachedReceiptExitCode(receipt));
      }

      // Phase 04a (§4.2 (5)): `test.env` sai quyền/owner ⇒ chặn TRƯỚC dispatch,
      // trước cả machine lock — thông điệp chỉ đường dẫn, không giá trị.
      const secrets = await preflightMissionTestEnvForRun(resolved.missionId);
      if (!secrets.ok) failRun("secrets_preflight", secrets.message, opts);
      // Phase 04b (§4.2 (11)/(12)): chỉ đọc stdin khi có cờ (không cờ ⇒ pipe
      // không bị tiêu thụ); TTY/sai dạng ⇒ chặn trước dispatch. Map chỉ ở RAM.
      let releaseSecretsStdin: ReleaseSecretsStdin | undefined;
      if (opts.releaseSecretsStdin === true) {
        const read = await readReleaseSecretsFromStdin();
        if (!read.ok) failRun("release_stdin_refused", read.message, opts);
        releaseSecretsStdin = read.values;
        addStdinValues(resolved.missionId, { ...read.values });
      }

      // C3: `last-run.json` chỉ khi --json/--detach (đường mặc định không đổi byte).
      const tracksLastRun = opts.json === true || opts.detachedChild === true;
      const runId = opts.runId ?? newRunId();
      const missionWorkspace = join(resolved.missionHome, "workspace");
      let result: RunMissionResult | undefined;
      let json: Awaited<ReturnType<typeof buildRunJsonResult>> | undefined;
      try {
        result = await runAutopilotRunCommand({
          workspaceRoot,
          missionId: resolved.missionId,
          missionHome: resolved.missionHome,
          controlRoot: resolved.controlRoot,
          repoFlag: opts.repo,
          machineLockPath: deps.machineLockPath,
          globalConfigPath: deps.globalConfigPath,
          maxTicks,
          log,
          releaseSecretsStdin,
          ignoreHangup: opts.detachedChild === true,
          // B3/N17: ghi trong `try` của startSupervisor; lỗi ghi chỉ log, không ném.
          onMachineLockAcquired: tracksLastRun
            ? lastRunOnLockAcquired({
                controlRoot: resolved.controlRoot,
                runId,
                detached: opts.detachedChild === true,
                logPath: opts.logPath,
                warn: (m) => log.warn(m),
              })
            : undefined,
        });
      } catch (err) {
        const kind = machineLockErrorKind(err);
        if (kind === undefined || opts.detachedChild !== true) throw err;
        failRun(kind, err instanceof Error ? err.message : String(err), opts);
      } finally {
        // N15: lỗi ⇒ không `result`, vẫn CAS theo run_id (lock đã nhả ở đây).
        if (tracksLastRun) {
          try {
            json = result
              ? await buildRunJsonResult({
                  missionId: resolved.missionId,
                  result,
                  controlRoot: resolved.controlRoot,
                  missionWorkspace,
                })
              : undefined;
            await writeLastRunExited(resolved.controlRoot, runId, json);
          } catch (err) {
            log.warn(`Không ghi được last-run.json: ${String(err)}`);
          }
        }
        // L5: đúng MỘT dòng stdout kể cả khi dựng kết quả lỗi hoặc run ném lỗi.
        if (opts.json === true) {
          const line = json ?? runJsonFallback(resolved.missionId, result);
          writeMachineReadableJson({ command: COMMAND, result: line });
        }
      }
      // H14 D15: 0 only when the ticket loop is done AND the release flow settled.
      const code = result === undefined ? 1 : runCommandExitCode(result);
      if (code !== 0) process.exit(code);
    });
}
